import {ReactElement} from "react";
import {useTheme} from "@emotion/react";
import {Options} from "highcharts";
import {i18n} from "i18next";

import {formatNumber, formatNumberGreaterThan1000} from "../utils/format";
import {slugifyChartTitle, stringifyJSXElement} from "../utils/slugify_chart_title";
import {formatTooltip} from "../utils/tooltips";

export const defaultOptions = (chartTitle: ReactElement, i18n: i18n): Options => {
    const theme = useTheme();

    return {
        chart: {
            backgroundColor: "transparent",
            numberFormatter: (val) => formatNumber(val),
            spacing: [10, 20, 20, 20],
            style: {
                fontFamily: "'Poppins', sans-serif"
            }
        },
        credits: {
            enabled: false
        },
        exporting: {
            enabled: false,
            fallbackToExportServer: false,
            sourceWidth: 800,
            tableCaption: stringifyJSXElement(chartTitle),
            filename: slugifyChartTitle(chartTitle),
            chartOptions: {
                chart: {
                    height: 450,
                    backgroundColor: "#fff",
                    events: {
                        load: function () {
                            this.renderer
                                .image("/static-charts/public/rynek-pierwotny-pl-big-data-logo.svg", 625, 388, 163, 63)
                                .add();
                        }
                    },
                    style: {
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: "10px",
                        fontWeight: "400"
                    },
                    spacingTop: 30,
                    spacingBottom: 30
                },
                title: {
                    align: "left",
                    margin: 20,
                    style: {
                        color: `${theme.colors.gray[700]}`,
                        fontSize: "13px",
                        fontWeight: "600"
                    },
                    widthAdjust: 0
                },
                subtitle: {
                    align: "left",
                    style: {
                        color: `${theme.colors.gray[700]}`,
                        fontSize: "10px"
                    },
                    verticalAlign: "bottom",
                    y: 20
                }
            },
            csv: {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                columnHeaderFormatter: function (_, key) {
                    if (!key) {
                        return i18n.t("common.date");
                    }
                    return false;
                }
            }
        },
        legend: {
            enabled: false,
            itemStyle: {
                fontFamily: "'Poppins', sans-serif",
                fontSize: "1.2rem",
                fontWeight: "700"
            }
        },
        navigation: {
            menuItemStyle: {
                fontFamily: "'Poppins', sans-serif",
                fontSize: "1.4rem",
                borderRadius: 4
            },
            menuItemHoverStyle: {
                background: "white",
                color: `${theme.colors.primary}`
            },
            menuStyle: {
                border: "0",
                padding: "0",
                borderRadius: 30,
                overflow: "hidden"
            }
        },
        plotOptions: {
            bar: {
                stacking: "normal",
                pointWidth: 8
            },
            column: {
                pointWidth: 8
            },
            line: {
                lineWidth: 3
            },
            map: {
                borderWidth: 0,
                nullColor: `${theme.colors.gray[200]}`,
                states: {
                    hover: {
                        brightness: 0
                    }
                }
            },
            pie: {
                dataLabels: {
                    enabled: false
                },
                innerSize: "69%"
            },
            series: {
                cursor: "pointer",
                marker: {
                    enabled: false
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                }
            },
            spline: {
                dataLabels: {
                    style: {
                        fontSize: "1.4rem",
                        color: "black",
                        fontWeight: "normal"
                    },
                    align: "center",
                    verticalAlign: "top"
                },
                enableMouseTracking: false,
                lineWidth: 3,
                states: {
                    hover: {
                        enabled: false
                    }
                }
            }
        },
        title: {
            text: ""
        },
        tooltip: {
            backgroundColor: "transparent",
            borderRadius: 0,
            enabled: true,
            formatter: function () {
                return formatTooltip(this);
            },
            footerFormat: "",
            hideDelay: 0,
            outside: true,
            shadow: false,
            shared: false,
            style: {
                fontSize: "1.4rem"
            },
            useHTML: true
        },
        xAxis: {
            gridLineWidth: 1,
            labels: {
                enabled: true,
                formatter: function () {
                    if (typeof this.value === "number") {
                        return formatNumber(this.value);
                    }
                    return this.value;
                },
                autoRotation: [-45],
                style: {
                    color: `${theme.colors.secondary}`,
                    fontSize: "1.1rem"
                }
            },
            lineWidth: 0,
            visible: false
        },
        yAxis: {
            gridLineWidth: 1,
            labels: {
                enabled: true,
                formatter: function () {
                    return formatNumberGreaterThan1000(this.value as number);
                },
                style: {
                    color: `${theme.colors.secondary}`,
                    fontSize: "1.2rem"
                }
            },
            lineWidth: 0,
            title: {
                text: null
            },
            visible: false
        }
    };
};
